import {Nullable} from '@fl/cmsch-fe-library';
import {isEqual} from 'lodash/fp';
import {SagaIterator} from 'redux-saga';
import {opt} from 'ts-opt';
import {select} from 'typed-redux-saga';

import {Farm} from 'api/gen/Farm';
import {OrganizationCenter} from 'api/gen/OrganizationCenter';
import {State} from 'app/setup';

import {allOptionValueArray} from '../../constants/consts';
import {simpleAdminSelector} from '../selector';

type BuildCentersType = 'USERS_FARM' | 'MANAGEMENT_ORGANIZATION_CENTER' | 'MANAGEMENT_ORGANIZATION_FARM';

type GetOptionsSelectorReturnType =
    | Array<OrganizationCenter>
    | Array<Farm>;

/**
 * Input for building the center options used in admin forms.
 */
export interface BuildSubjectManagementCenters {
    /** Controls which options collection is used. */
    type: BuildCentersType;
    /** Center ids selected in the form, if any. */
    centerIds: Nullable<Array<number>>;
}

const getOptionsSelector = (type: BuildCentersType): (state: State) => GetOptionsSelectorReturnType => {
    switch (type) {
        case 'USERS_FARM':
            return simpleAdminSelector.managementBreedersFarms;
        case 'MANAGEMENT_ORGANIZATION_CENTER':
            return simpleAdminSelector.managementOrganizationCenters;
        case 'MANAGEMENT_ORGANIZATION_FARM':
            return simpleAdminSelector.managementOrganizationFarms;
    }
};

/**
 * Builds management-center options for admin forms.
 *
 * @param params Center ids and the option source selector type.
 * @returns The normalized center ids for the form.
 */
export function* buildSubjectManagementCenterOptions({
    centerIds,
    type,
}: BuildSubjectManagementCenters): SagaIterator<BuildSubjectManagementCenters['centerIds']> {
    if (isEqual(centerIds, allOptionValueArray)) {
        const optionsSelector = getOptionsSelector(type);
        const allCenters = opt((yield* select(optionsSelector))).orCrash(`Centers for ${type} not found`);

        return allCenters.map(c => c.id);
    }

    return centerIds ?? [];
}
